import { FileText, Download, Trash2, Eye, Loader2, AlertTriangle, FolderOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DOC_STATUS, fmtSize } from "./constants";

export function DocumentsList({ docs, canWrite, onOpenDetail, onDownload, onRemove }) {
  return (
    <Card className="border-slate-200" data-testid="documents-list-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <FileText className="w-4 h-4" aria-hidden="true" />
          Documenti caricati {docs ? `(${docs.length})` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {docs === null ? (
          <p className="text-sm text-slate-500">Caricamento…</p>
        ) : docs.length === 0 ? (
          <div className="text-center py-10" data-testid="documents-empty">
            <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-3" aria-hidden="true" />
            <p className="text-sm text-slate-600 font-medium">Non hai ancora caricato documenti.</p>
            <p className="text-xs text-slate-500 mt-1">Puoi iniziare con una bolletta, un bilancio o una policy aziendale.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="documents-list">
            {docs.map((doc) => {
              const st = DOC_STATUS[doc.document_status] || DOC_STATUS.UPLOADED;
              return (
                <li key={doc.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`document-row-${doc.id}`}>
                  <FileText className="w-5 h-5 text-slate-400 shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate" data-testid={`document-name-${doc.id}`}>
                      {doc.name}
                      {doc.version > 1 && <span className="ml-1 text-xs text-slate-400">v{doc.version}</span>}
                    </p>
                    <p className="text-xs text-slate-500">
                      {fmtSize(doc.size)} · {new Date(doc.created_at).toLocaleDateString("it-IT")}
                      {doc.document_type && <span> · {doc.document_type}</span>}
                    </p>
                    {doc.period_warning && (
                      <p className="text-xs text-amber-700 flex items-center gap-1 mt-0.5" data-testid={`period-warning-${doc.id}`}>
                        <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                        {doc.period_warning.message}
                      </p>
                    )}
                    {doc.low_quality_scan && (
                      <p className="text-xs text-amber-700 mt-0.5">Il documento è poco leggibile.</p>
                    )}
                  </div>
                  {doc.document_status === "PROCESSING" && (
                    <span className="inline-flex items-center gap-1 text-xs text-sky-800" data-testid={`processing-${doc.id}`}>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                      Stiamo analizzando il documento…
                    </span>
                  )}
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${st.cls}`} data-testid={`document-status-${doc.id}`}>
                    {st.label}
                  </span>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => onOpenDetail(doc)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-green-800 hover:bg-green-50 rounded px-2 py-1.5 transition-colors"
                      data-testid={`document-open-${doc.id}`}>
                      <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                      Apri
                    </button>
                    <button type="button" onClick={() => onDownload(doc)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded px-2 py-1.5 transition-colors"
                      data-testid={`document-download-${doc.id}`}>
                      <Download className="w-3.5 h-3.5" aria-hidden="true" />
                      Scarica
                    </button>
                    {canWrite && (
                      <button type="button" onClick={() => onRemove(doc)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:bg-red-50 rounded px-2 py-1.5 transition-colors"
                        data-testid={`document-delete-${doc.id}`}>
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                        Elimina
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
